import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { Button, ButtonType } from "../Components/Button/Button";
import { InfoTooltip } from "../Components/InfoTooltip/InfoTooltip";
import { MdClose } from "react-icons/md";
import "./HoldoutPercentageModal.scss";
import { formatPercent } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { CampaignCell } from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";
import { applyRemainderCell, applyDefaultSplitsToCells, getCellShare } from "./MetaLiftStudyUtils";

interface HoldoutPercentageModalProps {
  show: boolean;
  onHide: () => void;
  campaignCells: CampaignCell[];
  onUpdateAll: (
    updatedCells: Array<{ id: string; testPercentage: number; controlPercentage: number }>
  ) => void;
}

const HoldoutPercentageModal: React.FC<HoldoutPercentageModalProps> = ({
  show,
  onHide,
  campaignCells,
  onUpdateAll,
}) => {
  const [localCells, setLocalCells] = useState<CampaignCell[]>([]);

  useEffect(() => {
    if (show) {
      setLocalCells([...campaignCells]);
    }
  }, [show, campaignCells]);

  const remainderIndex = localCells.length > 0 ? localCells.length - 1 : -1;

  const parsePercentage = (value: string): number => {
    const cleaned = value.replace(/%/g, "").trim();
    const numValue = parseFloat(cleaned) / 100;
    return isNaN(numValue) ? 0 : Math.min(1, numValue);
  };

  const handleTestChange = (cellId: string, value: string) => {
    const numValue = parsePercentage(value);
    setLocalCells(prev => {
      const remIdx = prev.length > 0 ? prev.length - 1 : -1;
      const remainderCellId = remIdx >= 0 ? prev[remIdx].id : null;
      const next = prev.map(cell =>
        cell.id === cellId ? { ...cell, testPercentage: numValue } : cell
      );
      if (remIdx >= 0 && cellId === remainderCellId) {
        const sumOthers = next
          .filter((_, i) => i !== remIdx)
          .reduce((s, c) => s + getCellShare(c), 0);
        const share = 1 - sumOthers;
        const newControl = Math.max(0, Math.min(1, share - numValue));
        return next.map((cell, i) =>
          i === remIdx ? { ...cell, controlPercentage: newControl } : cell
        );
      }
      return remIdx >= 0 ? applyRemainderCell(next, remIdx) : next;
    });
  };

  const handleControlChange = (cellId: string, value: string) => {
    const numValue = parsePercentage(value);
    setLocalCells(prev => {
      const remIdx = prev.length > 0 ? prev.length - 1 : -1;
      const remainderCellId = remIdx >= 0 ? prev[remIdx].id : null;
      const next = prev.map(cell =>
        cell.id === cellId ? { ...cell, controlPercentage: numValue } : cell
      );
      if (remIdx >= 0 && cellId === remainderCellId) {
        const sumOthers = next
          .filter((_, i) => i !== remIdx)
          .reduce((s, c) => s + getCellShare(c), 0);
        const share = 1 - sumOthers;
        const newTest = Math.max(0, Math.min(1, share - numValue));
        return next.map((cell, i) => (i === remIdx ? { ...cell, testPercentage: newTest } : cell));
      }
      return remIdx >= 0 ? applyRemainderCell(next, remIdx) : next;
    });
  };

  const handleSave = () => {
    const withRemainderApplied =
      remainderIndex >= 0 ? applyRemainderCell(localCells, remainderIndex) : localCells;
    const updatedCells = withRemainderApplied.map(cell => ({
      id: cell.id,
      testPercentage: cell.testPercentage,
      controlPercentage: cell.controlPercentage,
    }));
    onUpdateAll(updatedCells);
    onHide();
  };

  const handleResetToDefault = () => {
    if (localCells.length === 0) {
      return;
    }
    setLocalCells(applyDefaultSplitsToCells(localCells));
  };

  return (
    <Modal show={show} onHide={onHide} className="holdoutPercentageModal" size="lg" centered>
      <Modal.Header className="holdoutPercentageModalHeader">
        <div className="holdoutPercentageModalHeaderContent">
          <div className="holdoutPercentageModalTitleContainer">
            <h3 className="holdoutPercentageModalTitle">
              Holdout Percentages{" "}
              <InfoTooltip>
                {" "}
                Each test requires at least one cell and can include at most 5 cells. Multiple cells
                are best used to directly test competing strategies against each other, not to
                evaluate the relative performance of different campaigns. For example, if you wanted
                to evaluate the incremental performance of BAU conversion optimized campaigns
                against revenue optimized campaigns, a two-celled test would be the correct option.
                On the other hand, if you wanted to evaluate the incremental performance of
                prospecting campaigns and retargeting campaigns, you should configure two separate
                one-celled tests.
              </InfoTooltip>
            </h3>
            <div className="holdoutPercentageModalSubtitle">
              This determines the split between test and control for all cells.
            </div>
          </div>
          <button className="holdoutPercentageModalCloseButton" onClick={onHide}>
            <MdClose size={24} />
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className="holdoutPercentageModalBody">
        {localCells.map((cell, index) => (
          <div key={cell.id} className="holdoutPercentageModalCellRow">
            <div className="holdoutPercentageModalCellNumber">{index + 1}</div>
            <div className="holdoutPercentageModalCellName">
              {cell.name}
              {index === remainderIndex && localCells.length > 1 && (
                <span className="holdoutPercentageModalRemainderHint"> (adjusts to 100%)</span>
              )}
            </div>
            <div className="holdoutPercentageModalInputGroup">
              <div className="holdoutPercentageModalInputField">
                <label className="holdoutPercentageModalLabel">Test:</label>
                <Form.Control
                  type="text"
                  value={formatPercent(cell.testPercentage, 0)}
                  onChange={e => handleTestChange(cell.id, e.target.value)}
                  className="holdoutPercentageModalInput"
                />
              </div>
              <div className="holdoutPercentageModalInputField">
                <label className="holdoutPercentageModalLabel">Control:</label>
                <Form.Control
                  type="text"
                  value={formatPercent(cell.controlPercentage, 0)}
                  onChange={e => handleControlChange(cell.id, e.target.value)}
                  className="holdoutPercentageModalInput"
                />
              </div>
            </div>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer className="holdoutPercentageModalFooter">
        <button className="holdoutPercentageModalResetLink" onClick={handleResetToDefault}>
          Reset to default
        </button>
        <div className="holdoutPercentageModalFooterButtons">
          <Button type={ButtonType.FILLED} onClick={handleSave}>
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default HoldoutPercentageModal;
